import { Show, Text, View, hexColor } from "@lightningtv/solid";
import { For, createEffect, createSignal, untrack } from "solid-js";
import { playListPassword, playListUrl, playListUsername } from "../../../../state";
import axios from "axios";
import { Buffer } from "buffer";
import { fontFamily, isRTL } from "../../../translations/configuration";
import { decodeBase64UTF8 } from "../../../utils/encoding";
import { getProxyHeaders } from "../../../api/proxyHeaders";

const SelectedChannelEpg = (props: any) => {
  const [isFavChannel, setIsFavChannel] = createSignal(false);

  const [epgData, setEpgData] = createSignal<any>(null);
  const [firstProgram, setFirstProgram] = createSignal("");
  const [secondProgram, setSecondProgram] = createSignal("");
  const [thirdProgram, setThirdProgram] = createSignal("");

  const [firstProgramDescription, setFirstProgramDescription] = createSignal("");
  const [firstProgramStartTime, setFirstProgramStartTime] = createSignal("");
  const [firstProgramEndTime, setFirstProgramEndTime] = createSignal("");

  const [secondProgramStartTime, setSecondProgramStartTime] = createSignal("");
  const [thirdProgramStartTime, setThirdProgramStartTime] = createSignal("");

  const [progressPercent, setProgressPercent] = createSignal(0);

  const fetchEpg = () => {
    const username = untrack(playListUsername);
    const password = untrack(playListPassword);
    const playList = untrack(playListUrl);
    const stream_id = props.channel.stream_id;
    const vpnStatus = localStorage.getItem("vpn");
    const proxyUrl = localStorage.getItem("proxy_url");

    if (!stream_id) return;

    let apiUrl = `${playList}/player_api.php?username=${username}&password=${password}&action=get_short_epg&stream_id=${stream_id}`;

    if (vpnStatus === "on" && proxyUrl) {
      const bufferedUrl = Buffer.from(apiUrl, "utf-8").toString("base64");
      apiUrl = `${proxyUrl}${bufferedUrl}`;
    }

    axios
      .get(apiUrl, {
        headers: vpnStatus === "on" && proxyUrl ? getProxyHeaders() : undefined,
      })
      .then(response => {
        setEpgData(response.data);

        const listings = response.data.epg_listings;

        setFirstProgram(
          listings[0] ? decodeBase64UTF8(listings[0]?.title) : `Programs of ${props.channel.name}`,
        );
        setSecondProgram(
          listings[1] ? decodeBase64UTF8(listings[1]?.title) : `Programs of ${props.channel.name}`,
        );
        setThirdProgram(
          listings[2] ? decodeBase64UTF8(listings[2]?.title) : `Programs of ${props.channel.name}`,
        );
        setFirstProgramDescription(
          listings[0] ? decodeBase64UTF8(listings[0]?.description) : "No epg description",
        );
        setFirstProgramStartTime(
          listings[0]?.start_timestamp ? convertToTime(listings[0]?.start_timestamp) : "",
        );
        setFirstProgramEndTime(
          listings[0]?.stop_timestamp ? convertToTime(listings[0]?.stop_timestamp) : "",
        );
        setSecondProgramStartTime(
          listings[1]?.start_timestamp ? convertToTime(listings[1]?.start_timestamp) : "",
        );
        setThirdProgramStartTime(
          listings[2]?.start_timestamp ? convertToTime(listings[2]?.start_timestamp) : "",
        );

        // ── Compute progress percentage ──────────────────────────────
        if (listings[0]?.start_timestamp && listings[0]?.stop_timestamp) {
          const now = Math.floor(Date.now() / 1000);
          const start = listings[0].start_timestamp;
          const end = listings[0].stop_timestamp;
          const pct = Math.min(100, Math.max(0, Math.round(((now - start) / (end - start)) * 100)));
          setProgressPercent(pct);
        }
      })
      .catch(error => {
        setEpgData(null);
        console.error("Failed to fetch EPG data", error);
      });
  };

  createEffect(() => {
    const stream_id = props.channel?.stream_id;
    if (!stream_id) return;
    fetchEpg();
  });

  // Sync favorite state whenever the focused channel changes
  const syncFavState = () => {
    const currentPlaylistUrl = playListUrl();
    const existingChannels = localStorage.getItem("@favoriteChannels");
    const existingChannelsArray = existingChannels ? JSON.parse(existingChannels) : [];
    const streamId = props.channel?.stream_id;
    setIsFavChannel(
      existingChannelsArray.some(
        (c: any) => {
          const cId = c.data?.stream_id || c.stream_id;
          return cId === streamId && c.playlistUrl === currentPlaylistUrl;
        }
      )
    );
  };

  createEffect(() => {
    if (props.channel?.stream_id) {
      syncFavState();
    }
  });

  const handleFavToggle = () => {
    props.onFavToggle?.();
    // Re-read localStorage after parent updates it to sync the heart icon
    syncFavState();
  };

  const convertToTime = (timestamp: number) => {
    const date = new Date(timestamp * 1000);
    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");
    return `${hours}:${minutes}`;
  };

  const splitIntoRows = (text: string, maxChars: number) => {
    const rows: string[] = [];
    for (let i = 0; i < text.length; i += maxChars) {
      rows.push(text.slice(i, i + maxChars));
    }
    if (rows.length > 3) {
      rows[2] = rows[2].slice(0, maxChars - 3) + "...";
    }
    return rows.slice(0, 3);
  };

  const truncate = (text: string, max: number) =>
    text?.length > max ? `${text.substring(0, max)}...` : text;

  /** Use NotoSansArabic whenever the EPG string itself contains Arabic characters. */
  const epgFont = (text: string) =>
    /[؀-ۿ]/.test(text) ? "NotoSansArabic" : fontFamily();

  // Progress bar total width
  const BAR_W = 700;

  return (
    <View width={760} clipping x={0} {...props}>
      <Show when={firstProgram() && secondProgram()} fallback={<Text y={10} x={20} />}>

        <View x={24} y={20}>

          {/* ── Channel name ── */}
          <Text style={{
            fontSize: 30,
            color: hexColor("#f0f6fc"),
            fontWeight: isRTL() ? "normal" : "bold",
            fontFamily: fontFamily(),
          }}>
            {props.channel.name}
          </Text>

          {/* ── Current program title (blue, like screenshot) ── */}
          <Text y={46} style={{
            fontSize: 26,
            color: hexColor("#60a5fa"),
            fontFamily: epgFont(firstProgram()),
          }}>
            {truncate(firstProgram(), 52)}
          </Text>

          <View
            x={640} y={-10}
            width={50} height={50}
            ref={(el) => props.onFavRef?.(el)}     // ← expose ref to parent
            onUp={() => props.onFavUp?.()}         // ← focus preview on up
            onEnter={() => handleFavToggle()}
            onLeft={() => props.onFavLeft?.()}
            onRight={() => props.onFavRight?.()}
            style={{
              borderRadius: 30,
              color: hexColor("#15171b"),
              border: { width: 0, color: hexColor("#2563eb") },
              focus: { border: { width: 2, color: hexColor("#2563eb") } },
              transition: { color: { duration: 200 } },
            }}
          >
            <View
              x={10} y={10}
              width={30} height={30}
              src={
                isFavChannel()
                  ? "https://i.ibb.co/pRyqmgX/icons8-love-100-2.png"
                  : "https://i.ibb.co/kHcHCDb/icons8-love-100.png"
              }
            />
          </View>

          {/* ── Start / End times ── */}
          <Show when={firstProgramStartTime() && firstProgramEndTime()}>
            <View y={86} style={{ flexDirection: "row" }} gap={20}>
              <Text style={{ fontSize: 22, color: hexColor("#9ca3af"), fontFamily: fontFamily() }}>
                {firstProgramStartTime()}
              </Text>
              <Text x={640} style={{ fontSize: 22, color: hexColor("#9ca3af"), fontFamily: fontFamily() }}>
                {firstProgramEndTime()}
              </Text>
            </View>
          </Show>

          {/* ── Progress bar ── */}
          <View y={118} width={BAR_W} height={6} borderRadius={3} color={hexColor("#1e2a3a")}>
            <View
              width={Math.round((progressPercent() / 100) * BAR_W)}
              height={6}
              borderRadius={3}
              color={hexColor("#2563eb")}
            />
          </View>

          {/* ── Percentage label ── */}
          <View x={570} y={132} style={{ flexDirection: "row" }}>
            {/* spacer to push percentage to the right */}
            <Text style={{ fontSize: 0, color: hexColor("#00000000") }}>
              {"                                                                                                   "}
            </Text>
            <Text style={{
              fontSize: 20,
              color: hexColor("#60a5fa"),
              fontWeight: isRTL() ? "normal" : "bold",
              fontFamily: fontFamily(),
            }}>
              {progressPercent()}% Complete
            </Text>
          </View>

          {/* ── Description ── */}
          <Show when={firstProgramDescription()}>
            <For each={splitIntoRows(firstProgramDescription(), 80)}>
              {(row, index) => (
                <Text y={162 + index() * 28} style={{
                  fontSize: 20,
                  color: hexColor("#9ca3af"),
                  fontFamily: epgFont(row),
                }}
                  numberOfLines={3}>
                  {row}
                </Text>
              )}
            </For>
          </Show>

          {/* ── Coming up next header ── */}
          <Text y={260} style={{
            fontSize: 20,
            color: hexColor("#6b7280"),
            fontWeight: isRTL() ? "normal" : "bold",
            fontFamily: fontFamily(),
            letterSpacing: 1,
          }}>
            COMING UP NEXT
          </Text>

          {/* ── Program 2 ── */}
          <Show when={secondProgram()}>
            <View y={300} style={{ flexDirection: "row" }} gap={16}>
              {/* Time badge */}
              <Show when={secondProgramStartTime()}>
                <View
                  width={74} height={34}
                  borderRadius={8}
                  color={hexColor("#1a1d24")}
                >
                  <Text x={10} y={8} style={{
                    fontSize: 18,
                    color: hexColor("#9ca3af"),
                    fontFamily: fontFamily(),
                  }}>
                    {secondProgramStartTime()}
                  </Text>
                </View>
              </Show>
              <Text y={6} x={80} style={{
                fontSize: 20,
                color: hexColor("#ffffff"),
                fontFamily: epgFont(secondProgram()),
                fontWeight: isRTL() ? "normal" : "bold",
              }}>
                {truncate(secondProgram(), 46)}
              </Text>
            </View>
          </Show>

          {/* ── Program 3 ── */}
          <Show when={thirdProgram()}>
            <View y={350} style={{ flexDirection: "row" }} gap={16}>
              <Show when={thirdProgramStartTime()}>
                <View
                  width={74} height={34}
                  borderRadius={8}
                  color={hexColor("#1a1d24")}
                >
                  <Text x={10} y={8} style={{
                    fontSize: 18,
                    color: hexColor("#9ca3af"),
                    fontFamily: fontFamily(),
                  }}>
                    {thirdProgramStartTime()}
                  </Text>
                </View>
              </Show>
              <Text x={80} y={6} style={{
                fontSize: 20,
                color: hexColor("#ffffff"),
                fontFamily: epgFont(thirdProgram()),
                fontWeight: isRTL() ? "normal" : "bold",
              }}>
                {truncate(thirdProgram(), 46)}
              </Text>
            </View>
          </Show>

        </View>
      </Show>
    </View>
  );
};

export default SelectedChannelEpg;